import React, { useEffect } from "react";
import Metatag from "../../components/Metatag/Metatag";

const Aboutus = () => {
  useEffect(() => {
    // Scroll to the top when the page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Metatag
        title="About Us | Megavolt Technical Services"
        description="Megavolt Technical Services Co. LLC is a leading MEP, HVAC, electrical, plumbing, civil, fit-out, and DEWA approval company in Dubai, UAE, delivering complete engineering and contracting solutions."
        link="aboutus"
      />
      <div className="relative bg-[#f8fafc] overflow-hidden min-h-screen pb-20">

        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-[500px] h-[500px] bg-primary rounded-full mix-blend-multiply filter blur-[150px] opacity-20 pointer-events-none"></div>
        <div className="absolute top-[20%] left-0 -translate-x-1/3 w-[600px] h-[600px] bg-secondary rounded-full mix-blend-multiply filter blur-[150px] opacity-10 pointer-events-none"></div>

        {/* Header Section */}
        <div className="relative text-center items-center pt-20 pb-12 z-10">
          <span className="text-primary font-bold tracking-widest text-sm uppercase mb-3 block">Discover Our Story</span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#090a0e] uppercase tracking-tight">
            About <span className="text-transparent bg-clip-text bg-brand-gradient">US</span>
          </h1>
          <p className="mt-4 text-lg font-medium text-gray-600 px-4 max-w-2xl mx-auto">
            Powering Projects with Precision, Quality & Compliance
          </p>
          <div className="w-24 h-1.5 bg-brand-gradient mx-auto mt-6 rounded-full"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 w-full max-w-[1200px]">

          {/* Main Intro Block */}
          <div className="bg-white/80 backdrop-blur-xl border border-white/50 rounded-3xl p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-16">
            <div className="space-y-6 text-[#4b5563] text-lg leading-relaxed text-left md:text-justify">
              <p>
                <strong className="text-black text-xl">Megavolt Technical Services Co. LLC</strong> is a trusted Electrical Services and DEWA Approved Contractor in Dubai, UAE, delivering reliable engineering solutions for residential, commercial, retail, and industrial projects.
              </p>
              <p>
                With over 7 years of industry experience, we specialize in Electrical engineering andPlumbing, DEWA services. Our team is committed to providing high-quality workmanship, cost-effective solutions, regulatory compliance, and timely project delivery — all while maintaining a strong focus on client satisfaction.
              </p>
              <p>
                At Megavolt, we believe every successful project starts with smart planning, technical expertise, and proper approvals. From design and installation to maintenance and authority approvals, we offer end-to-end support tailored to your project requirements and budget.
              </p>
            </div>
          </div>

          {/* Who We Are & Mission */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <div className="bg-white/60 backdrop-blur-lg rounded-3xl p-8 md:p-10 border border-white shadow-sm hover:shadow-md transition-shadow duration-300">
              <h2 className="text-[#090a0e] text-3xl font-extrabold mb-6 capitalize flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-brand-gradient flex items-center justify-center text-white text-sm">✦</span>
                Who We Are
              </h2>
              <p className="text-[#4b5563] text-lg leading-relaxed mb-6">
                Megavolt Technical Services Co. LLC is a Dubai-based engineering and contracting company built on the values of quality, safety, transparency, and professionalism.
              </p>
              <p className="text-[#4b5563] text-lg leading-relaxed">
                We provide complete Electrical & Plumbing solutions with a strong emphasis on feasible design, efficient execution, and compliance with Dubai authority standards. Our expertise enables us to support projects of all scales — from small fit-out works to large commercial and industrial developments.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#090a0e] to-[#1a1c23] rounded-3xl p-8 md:p-10 text-white shadow-xl relative overflow-hidden group">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary opacity-20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
              <h3 className="font-extrabold text-2xl mb-4 text-primary uppercase tracking-wide">Our Mission is Simple</h3>
              <p className="text-xl md:text-2xl font-light leading-relaxed relative z-10 italic">
                "To deliver dependable engineering and technical services that add long-term value to every project."
              </p>
              <div className="mt-12 flex items-center justify-end z-10 relative">
                <div className="w-16 h-1 bg-gradient-to-r from-transparent to-primary rounded-full"></div>
              </div>
            </div>
          </div>

          {/* What We Do - New Premium Grid */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="font-extrabold text-3xl md:text-4xl text-[#090a0e] uppercase tracking-tight">What We Do</h2>
              <p className="text-lg text-gray-500 mt-4 max-w-2xl mx-auto">
                We offer a complete range of engineering and contracting services across Dubai and the UAE, including:
              </p>
              <div className="w-16 h-1 bg-gray-300 mx-auto mt-6 rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[
                {
                  id: "02",
                  title: "Electrical Engineering Systems",
                  desc: "Our electrical division provides safe, efficient, and compliant electrical solutions:",
                  items: ["HV & LV Systems", "Substation and LV Room Systems", "Earthing & Lightning Protection Systems", "Lighting & Power Layout Design", "Substation and LV Room Design", "BMS & Control Systems", "Security & Access Control Systems"]
                },
                {
                  id: "03",
                  title: "Plumbing Engineering Systems",
                  desc: "Complete plumbing and fire protection solutions designed for performance and compliance:",
                  items: ["Hot & Cold Water Systems", "Above & Below Ground Drainage Systems", "Water Supply & Drainage Layout Design"]
                },
                {
                  id: "04",
                  title: "DEWA & Dubai Approval Services",
                  desc: "Navigating approvals in Dubai can be complex — we make it simple:",
                  items: ["NOC Submission", "Electricity & Water Application", "Electricity Fit-Out Connection", "Electricity/Water Network Modification", "Power Upgradation Services", "New & Upgrade Substation Services"]
                }
              ].map((service, index) => (
                <div
                  key={index}
                  className="group relative bg-white/80 backdrop-blur-xl border border-white/50 rounded-2xl p-8 hover:bg-white transition-all duration-500 ease-out hover:-translate-y-2 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(247,177,45,0.15)] overflow-hidden flex flex-col h-full"
                >
                  {/* Top line gradient expands on hover */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  {/* Watermark */}
                  <div className="absolute -bottom-4 -right-2 text-[120px] font-black text-gray-100/60 group-hover:text-primary/5 transition-all duration-500 select-none z-0 pointer-events-none transform group-hover:-translate-x-2">
                    {service.id}
                  </div>

                  <div className="relative z-10 flex-grow">
                    <div className="w-14 h-14 bg-gray-50 flex-shrink-0 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-brand-gradient transition-all duration-500 shadow-sm border border-gray-100 group-hover:border-transparent group-hover:rotate-6">
                      <span className="text-xl font-bold text-primary group-hover:text-white transition-colors duration-500">
                        {service.id}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-[#090a0e] mb-3 group-hover:text-primary transition-colors duration-300">
                      {service.title}
                    </h3>

                    <p className="text-[#6b7280] text-sm mb-4 leading-relaxed">
                      {service.desc}
                    </p>

                    <ul className="space-y-2 mb-4">
                      {service.items.map((item, i) => (
                        <li key={i} className="flex items-start text-sm text-gray-700">
                          <span className="text-primary mr-2 font-bold mt-0.5">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {service.extra && (
                    <div className="relative z-10 mt-auto pt-4 border-t border-gray-100">
                      <p className="text-sm text-gray-500 italic font-medium">{service.extra}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Why Choose Us & Commitment */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
            {/* Why Choose Us */}
            <div className="lg:col-span-7 bg-white/70 backdrop-blur-xl border border-white rounded-3xl p-8 md:p-10 shadow-sm hover:shadow-md transition-shadow duration-300 relative overflow-hidden">
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-slate-100 rounded-full blur-[80px] -z-10"></div>
              <h2 className="text-[#090a0e] text-3xl font-extrabold mb-4">Why Choose Megavolt Technical Services Co. LLC?</h2>
              <p className="text-[#4b5563] text-lg leading-relaxed mb-8">
                Choosing the right technical partner is critical to the success of any project. At Megavolt, we combine engineering expertise, authority approval knowledge, and on-site execution capability to deliver seamless project outcomes.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  "7+ Years of Industry Experience",
                  "Expert Team of Engineers & Specialists",
                  "Dubai Authority & DEWA Approval Expertise",
                  "Cost-Effective Engineering Solutions",
                  "High Standards of Quality & Safety",
                  "Timely Delivery & Professional Execution",
                  "Client-Focused Approach",
                  "Strong Understanding of UAE Market"
                ].map((point, idx) => (
                  <div key={idx} className="flex items-center space-x-3 bg-white p-3 rounded-xl shadow-[0_2px_10px_-3px_rgba(0,0,0,0.05)] border border-gray-100 hover:border-primary/30 transition-colors">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                    </div>
                    <span className="text-sm font-semibold tracking-tight text-gray-800">{point}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <p className="text-[#090a0e] text-[15px] font-semibold italic text-gray-600">
                  We are committed to delivering solutions that are not only technically sound but also practical, compliant, and budget-conscious.
                </p>
              </div>
            </div>

            {/* Our Commitment */}
            <div className="lg:col-span-5 bg-[#090a0e] rounded-3xl p-8 md:p-10 text-white shadow-xl relative overflow-hidden group">
              {/* decorative mesh gradient effect inside */}
              <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/20 via-[#090a0e] to-[#090a0e] -z-10"></div>
              <h2 className="text-primary text-3xl font-extrabold mb-6 capitalize flex items-center gap-3">
                Our Commitment
              </h2>
              <p className="text-gray-300 text-[17px] mb-6 leading-relaxed">At Megavolt, we are committed to:</p>
              <ul className="space-y-4 mb-8">
                {[
                  "Delivering high-quality engineering and contracting services",
                  "Maintaining 100% client satisfaction",
                  "Ensuring compliance with Dubai regulations and authority approvals",
                  "Offering innovative, reliable, and cost-effective solutions",
                  "Building long-term relationships through trust and performance"
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <div className="w-2 h-2 rounded-full bg-brand-gradient mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-300 text-[15px] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="relative z-10 p-5 mt-auto bg-white/5 border border-white/10 rounded-2xl backdrop-blur-sm">
                <p className="text-white text-[15px] font-medium italic">
                  "We don’t just execute projects — we build solutions that are designed to perform, last, and add value."
                </p>
              </div>
            </div>
          </div>

          {/* CTA Banner */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl group border border-white/20">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-brand-gradient z-0 transition-transform duration-700 group-hover:scale-105"></div>
            {/* Pattern overlay */}
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent z-0 bg-[length:20px_20px]"></div>

            <div className="relative z-10 px-6 py-16 md:px-12 text-center flex flex-col items-center">
              <h2 className="text-white text-3xl md:text-5xl font-extrabold mb-6 drop-shadow-sm">Build with Confidence</h2>
              <p className="text-white/95 text-lg md:text-xl font-medium leading-relaxed max-w-4xl mx-auto mb-10 drop-shadow-[0_2px_2px_rgba(0,0,0,0.1)]">
                At Megavolt Technical Services Co. LLC, we bring together engineering excellence, technical precision, and approval expertise to help your project move forward smoothly and successfully.
              </p>
              <div className="bg-white/95 text-[#090a0e] px-8 py-5 md:py-6 rounded-2xl font-bold text-lg md:text-xl max-w-3xl shadow-xl border border-white/50 backdrop-blur-md">
                If you are looking for a dependable partner for Electrical works, Plumbing Engineering or Dubai authority approvals,
                <span className="text-primary whitespace-nowrap ml-2">Megavolt is here to deliver.</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Aboutus;
